/**
 * Admin Panel API — wrapper around all /api/admin/* endpoints.
 * Every function returns the response data directly (throws on error).
 */
import client from './API';

// ── Me ────────────────────────────────────────────────────────────────────────
export const getMe = () =>
  client.get('/api/admin/me/').then((r) => r.data);

// ── Overview (atoms_admin only) ───────────────────────────────────────────────
export const getOverview = () =>
  client.get('/api/admin/overview/').then((r) => r.data);

// ── User Modules ──────────────────────────────────────────────────────────────
export const getUserModules = (userPk) =>
  client.get(`/api/admin/users/${userPk}/modules/`).then((r) => r.data);

export const saveUserModules = (userPk, pageIds) =>
  client.post(`/api/admin/users/${userPk}/modules/`, { page_ids: pageIds }).then((r) => r.data);

// ── Organisations ─────────────────────────────────────────────────────────────
export const getOrgs = () =>
  client.get('/api/admin/organisations/').then((r) => r.data);

export const createOrg = (data) =>
  client.post('/api/admin/organisations/', data).then((r) => r.data);

export const updateOrg = (orgId, data) =>
  client.patch(`/api/admin/organisations/${orgId}/`, data).then((r) => r.data);

export const deleteOrg = (orgId) =>
  client.delete(`/api/admin/organisations/${orgId}/`).then((r) => r.data);

// ── Push notifications ────────────────────────────────────────────────────────
export const sendAdminPush = (payload) =>
  client.post('/api/admin/push/', payload).then((r) => r.data);

// ── Machines ──────────────────────────────────────────────────────────────────
export const getMachines = () =>
  client.get('/api/admin/machines/').then((r) => r.data);

export const getMachine = (machinePk) =>
  client.get(`/api/admin/machines/${machinePk}/`).then((r) => r.data);

export const createMachine = (data) =>
  client.post('/api/admin/machines/', data).then((r) => r.data);

export const updateMachine = (machinePk, data) =>
  client.patch(`/api/admin/machines/${machinePk}/`, data).then((r) => r.data);

export const deleteMachine = (machinePk) =>
  client.delete(`/api/admin/machines/${machinePk}/`).then((r) => r.data);

// ── Machine Channels ──────────────────────────────────────────────────────────
export const getMachineChannels = (machinePk) =>
  client.get(`/api/admin/machines/${machinePk}/channels/`).then((r) => r.data);

export const createChannel = (machinePk, data) =>
  client.post(`/api/admin/machines/${machinePk}/channels/`, data).then((r) => r.data);

export const updateChannel = (machinePk, channelPk, data) =>
  client
    .patch(`/api/admin/machines/${machinePk}/channels/${channelPk}/`, data)
    .then((r) => r.data);

export const deleteChannel = (machinePk, channelPk) =>
  client
    .delete(`/api/admin/machines/${machinePk}/channels/${channelPk}/`)
    .then((r) => r.data);

// ── Machine Shares ────────────────────────────────────────────────────────────
export const getMachineShares = (machinePk) =>
  client.get(`/api/admin/machines/${machinePk}/shares/`).then((r) => r.data);

export const createShare = (machinePk, data) =>
  client.post(`/api/admin/machines/${machinePk}/shares/`, data).then((r) => r.data);

export const updateShare = (machinePk, sharePk, data) =>
  client
    .patch(`/api/admin/machines/${machinePk}/shares/${sharePk}/`, data)
    .then((r) => r.data);

export const deleteShare = (machinePk, sharePk) =>
  client
    .delete(`/api/admin/machines/${machinePk}/shares/${sharePk}/`)
    .then((r) => r.data);

// ── Users ─────────────────────────────────────────────────────────────────────
export const getUsers = () =>
  client.get('/api/admin/users/').then((r) => r.data);

export const getUser = (userPk) =>
  client.get(`/api/admin/users/${userPk}/`).then((r) => r.data);

export const createUser = (data) =>
  client.post('/api/admin/users/', data).then((r) => r.data);

export const updateUser = (userPk, data) =>
  client.patch(`/api/admin/users/${userPk}/`, data).then((r) => r.data);

export const deleteUser = (userPk) =>
  client.delete(`/api/admin/users/${userPk}/`).then((r) => r.data);

export const toggleUser = (userPk) =>
  client.post(`/api/admin/users/${userPk}/toggle/`).then((r) => r.data);

// ── User ↔ Machines ───────────────────────────────────────────────────────────
export const getUserMachines = (userPk) =>
  client.get(`/api/admin/users/${userPk}/machines/`).then((r) => r.data);

export const removeUserMachine = (userPk, machinePk) =>
  client
    .delete(`/api/admin/users/${userPk}/machines/${machinePk}/`)
    .then((r) => r.data);

// ── User Channel Config ───────────────────────────────────────────────────────
export const getUserChannelConfig = (userPk, machinePk) =>
  client
    .get(`/api/admin/users/${userPk}/config/${machinePk}/`)
    .then((r) => r.data);

export const saveUserChannelConfig = (userPk, machinePk, data) =>
  client
    .post(`/api/admin/users/${userPk}/config/${machinePk}/`, data)
    .then((r) => r.data);

// ── Available Channels ────────────────────────────────────────────────────────
export const getAvailableChannels = (machinePk) =>
  client
    .get(`/api/admin/available-channels/${machinePk}/`)
    .then((r) => r.data);

// ── Alarm Rules ───────────────────────────────────────────────────────────────
export const getAlarmRules = (machinePk) =>
  client.get(`/api/admin/machines/${machinePk}/alarm-rules/`).then((r) => r.data);

export const createAlarmRule = (machinePk, data) =>
  client.post(`/api/admin/machines/${machinePk}/alarm-rules/`, data).then((r) => r.data);

export const updateAlarmRule = (machinePk, rulePk, data) =>
  client.patch(`/api/admin/machines/${machinePk}/alarm-rules/${rulePk}/`, data).then((r) => r.data);

export const deleteAlarmRule = (machinePk, rulePk) =>
  client.delete(`/api/admin/machines/${machinePk}/alarm-rules/${rulePk}/`).then((r) => r.data);

// ── Calculated Channels ───────────────────────────────────────────────────────
export const getCalculatedChannels = (machinePk) =>
  client.get(`/api/admin/machines/${machinePk}/calculated-channels/`).then((r) => r.data);

export const createCalculatedChannel = (machinePk, data) =>
  client.post(`/api/admin/machines/${machinePk}/calculated-channels/`, data).then((r) => r.data);

export const updateCalculatedChannel = (machinePk, calcPk, data) =>
  client.patch(`/api/admin/machines/${machinePk}/calculated-channels/${calcPk}/`, data).then((r) => r.data);

export const deleteCalculatedChannel = (machinePk, calcPk) =>
  client.delete(`/api/admin/machines/${machinePk}/calculated-channels/${calcPk}/`).then((r) => r.data);
